/**
 * ADR-0013 — Expiry Owner (Inventory).
 *
 * „Bald ablaufend“ = Conservation-Schwellen (14 / 14 / 30 / 60).
 * 7 Tage = nur Urgency-Tier (URGENT), kein alleiniger Filter.
 */

import { calculateDaysUntilExpiry } from '../../../utils/dateCalculations';

export const EXPIRATION_THRESHOLDS = {
  PERISSABLE: 14,
  COURTE: 14,
  MOYENNE: 30,
  LONGUE: 60
};

/** UI select values (order = Sans date → longue → moyenne → périssable). */
export const CONSERVATION_OPTIONS = Object.freeze([
  'SANS_DATE',
  'LONGUE',
  'MOYENNE',
  'PERISSABLE'
]);

export const EXPIRY_URGENT_DAYS = 7;
export const EXPIRY_DEFAULT_THRESHOLD = 60;

/**
 * Categories where shelf-life type is N/A (always sans date).
 * All other catalog categories may set LONGUE / MOYENNE / PERISSABLE.
 */
const CATEGORIES_WITHOUT_CONSERVATION_SELECTOR = new Set([
  'electronics',
  'clothing',
  'books-media',
  'jewelry-watches',
  'hardware',
  'construction-diy',
  'vehicles-motorcycles',
  'automotive',
  'office-business-school',
  'music-instruments',
  'art-crafts',
  'textiles-fabrics',
  'restaurant-menu'
]);

/**
 * @param {string} [category]
 * @returns {boolean}
 */
export function categorySupportsConservationSelector(category) {
  if (!category || typeof category !== 'string') return false;
  return !CATEGORIES_WITHOUT_CONSERVATION_SELECTOR.has(category);
}

/**
 * PERISSABLE / MOYENNE / LONGUE (and legacy COURTE) require an expiration date on receive.
 * SANS_DATE and empty → optional / no alerts.
 *
 * @param {string} [conservation]
 * @returns {boolean}
 */
export function requiresExpirationDate(conservation) {
  if (conservation == null || conservation === '') return false;
  return conservation !== 'SANS_DATE';
}

/**
 * @param {{ conservation?: string }|null|undefined} product
 * @returns {boolean}
 */
export function productRequiresExpirationDate(product) {
  return requiresExpirationDate(product?.conservation);
}

/**
 * First purchase/order line that needs but lacks an expiration date.
 *
 * @param {Array<{ productId?: *, expirationDate?: * }>} lines
 * @param {Map<string, object>|Array|object} productsOrById
 * @returns {object|null}
 */
export function findLineMissingRequiredExpiration(lines = [], productsOrById = null) {
  const list = Array.isArray(lines) ? lines : [];
  if (list.length === 0) return null;

  const getProduct = (productId) => {
    if (!productsOrById) return null;
    const key = String(productId ?? '');
    if (productsOrById instanceof Map) return productsOrById.get(key) || null;
    if (Array.isArray(productsOrById)) {
      return productsOrById.find((p) => String(p?.id ?? p?.productId) === key) || null;
    }
    if (typeof productsOrById === 'object') {
      return productsOrById[key] || productsOrById[productId] || null;
    }
    return null;
  };

  for (const line of list) {
    const product = getProduct(line?.productId);
    if (!productRequiresExpirationDate(product)) continue;
    const exp = line?.expirationDate;
    if (exp == null || String(exp).trim() === '') return line;
  }
  return null;
}

/**
 * @param {string} [conservation]
 * @returns {number|null} null = keine Alerts (SANS_DATE)
 */
export function getExpiryAlertThreshold(conservation) {
  if (conservation === 'SANS_DATE') return null;
  return EXPIRATION_THRESHOLDS[conservation] || EXPIRY_DEFAULT_THRESHOLD;
}

/**
 * @param {{ conservation?: string, expirationDate?: string|Date, daysUntilExpiry?: number }} params
 * @returns {boolean}
 */
export function isExpiringSoon({
  conservation,
  expirationDate,
  daysUntilExpiry: daysArg
} = {}) {
  if (conservation === 'SANS_DATE') return false;
  const daysUntilExpiry =
    daysArg != null ? daysArg : calculateDaysUntilExpiry(expirationDate);
  if (daysUntilExpiry == null || daysUntilExpiry <= 0) return false;
  const threshold = getExpiryAlertThreshold(conservation);
  if (threshold == null) return false;
  return daysUntilExpiry <= threshold;
}

/**
 * @param {{ conservation?: string, daysUntilExpiry?: number, expirationDate?: string|Date }} params
 * @returns {'URGENT'|'ATTENTION'|'SURVEILLER'|null}
 */
export function classifyExpiryUrgency({
  conservation,
  daysUntilExpiry: daysArg,
  expirationDate
} = {}) {
  const daysUntilExpiry =
    daysArg != null ? daysArg : calculateDaysUntilExpiry(expirationDate);
  if (daysUntilExpiry == null || daysUntilExpiry <= 0) return null;
  if (daysUntilExpiry <= EXPIRY_URGENT_DAYS) return 'URGENT';
  const threshold = getExpiryAlertThreshold(conservation) ?? EXPIRY_DEFAULT_THRESHOLD;
  if (daysUntilExpiry <= threshold) return 'ATTENTION';
  return 'SURVEILLER';
}

/**
 * Lots innerhalb der Conservation-Alert-Schwelle (Manager-KPI „expiring soon“).
 *
 * @param {{ lots?: Array|object, products?: Array, limit?: number|null }} params
 * @returns {Array<{ name: string, lotId: string, batchNumber: string, expiryDate: *, daysLeft: number, quantity: number, productId: *, location: string, conservation: string }>}
 */
export function listExpiringLots({ lots = {}, products = [], limit = null } = {}) {
  const lotsArray = Array.isArray(lots) ? lots : Object.entries(lots || {}).map(([id, lot]) => ({
    ...lot,
    id: lot?.id ?? id
  }));
  const productById = new Map((products || []).map((p) => [String(p.id), p]));
  const rows = [];

  for (const lot of lotsArray) {
    if (!lot?.expirationDate) continue;
    const qty = Number(lot.remainingQuantity || 0);
    if (qty <= 0) continue;

    const product = productById.get(String(lot.productId));
    const conservation =
      lot.conservationType || product?.conservation || 'PERISSABLE';
    const daysLeft = calculateDaysUntilExpiry(lot.expirationDate);
    if (!isExpiringSoon({ conservation, daysUntilExpiry: daysLeft })) continue;

    rows.push({
      name: product?.name || `Product ${lot.productId}`,
      lotId: String(lot.id ?? ''),
      batchNumber: lot.batchNumber || `Lot ${lot.id ?? ''}`,
      expiryDate: lot.expirationDate,
      daysLeft,
      quantity: qty,
      productId: lot.productId,
      location: lot.location || 'Principal',
      conservation
    });
  }

  rows.sort((a, b) => a.daysLeft - b.daysLeft);
  return limit != null ? rows.slice(0, limit) : rows;
}
